import { zodResolver } from "@hookform/resolvers/zod";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import type { OrgResponse } from "@bw/shared/types/generated";

export type OrgActionVariables =
  | Readonly<{
      kind: "approve" | "start-review" | "reactivate";
      orgId: string;
      reason?: never;
    }>
  | Readonly<{
      kind: "reject" | "suspend";
      orgId: string;
      reason: string;
    }>;

export type ActiveOrgAction = Pick<OrgActionVariables, "kind" | "orgId">;

export interface OrgActionError {
  orgId: string;
  kind: OrgActionVariables["kind"];
  message: string;
}

interface ReasonDraft {
  reason: string;
}

function ReasonDialog({
  open,
  kind,
  pending,
  failed,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  kind: "reject" | "suspend";
  pending: boolean;
  failed: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}) {
  const rejection = kind === "reject";
  const schema = z.object({
    reason: z
      .string()
      .max(
        rejection ? 1000 : Number.MAX_SAFE_INTEGER,
        "Use 1,000 characters or fewer.",
      )
      .refine((value) => value.trim().length > 0, {
        message: `Enter a reason for ${rejection ? "rejection" : "suspension"}.`,
      }),
  });
  const { register, watch, handleSubmit, reset } = useForm<ReasonDraft>({
    resolver: zodResolver(schema, undefined, { mode: "sync" }),
    mode: "onChange",
    defaultValues: { reason: "" },
  });
  const reason = watch("reason");
  const validation = schema.safeParse({ reason });
  const reasonError = validation.success
    ? undefined
    : validation.error.issues[0]?.message;
  const title = rejection ? "Reject organization" : "Suspend organization";

  useEffect(() => {
    if (!open && !pending) reset({ reason: "" });
  }, [open, pending, reset]);

  function dismiss() {
    if (!pending) onCancel();
  }

  return (
    <Dialog
      open={open}
      onClose={dismiss}
      fullWidth
      maxWidth="sm"
      aria-labelledby={`${kind}-organization-title`}
    >
      <Box
        component="form"
        noValidate
        onSubmit={handleSubmit((draft) => onConfirm(draft.reason.trim()))}
      >
        <DialogTitle id={`${kind}-organization-title`}>{title}</DialogTitle>
        <DialogContent>
          <TextField
            {...register("reason")}
            autoFocus
            fullWidth
            multiline
            minRows={3}
            disabled={pending}
            label={`Reason for ${rejection ? "rejection" : "suspension"}`}
            placeholder={`Reason for ${rejection ? "rejection" : "suspension"}...`}
            error={reason.length > 0 && !validation.success}
            helperText={reason.length > 0 ? reasonError : undefined}
            slotProps={
              rejection ? { htmlInput: { maxLength: 1000 } } : undefined
            }
          />
          {failed && (
            <Alert severity="error" role="alert" sx={{ mt: 2 }}>
              {rejection
                ? "The rejection could not be completed. Review the reason and try again."
                : "The suspension could not be completed. Review the reason and try again."}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            type="button"
            onClick={dismiss}
            disabled={pending}
          >
            Cancel
          </Button>
          <Button
            size="small"
            type="submit"
            color="error"
            variant="contained"
            disabled={pending || !validation.success}
            sx={{ "&&": { pointerEvents: "auto" } }}
          >
            {pending ? `${rejection ? "Rejecting" : "Suspending"}…` : title}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

function ApprovalDialog({
  open,
  pending,
  failed,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  pending: boolean;
  failed: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  function dismiss() {
    if (!pending) onCancel();
  }

  return (
    <Dialog
      open={open}
      onClose={dismiss}
      fullWidth
      maxWidth="sm"
      aria-labelledby="approve-launch-title"
    >
      <DialogTitle id="approve-launch-title">Approve launch</DialogTitle>
      <DialogContent>
        <DialogContentText>
          This approves the organization for launch. Facilities retain their
          current approval status and are reviewed separately.
        </DialogContentText>
        {failed && (
          <Alert severity="error" role="alert" sx={{ mt: 2 }}>
            Launch approval could not be completed. Review the blockers and try
            again.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={dismiss} disabled={pending}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={onConfirm}
          disabled={pending}
          sx={{ "&&": { pointerEvents: "auto" } }}
        >
          Approve launch
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function OrgStatusActionsPanel({
  org,
  actionPending,
  activeAction,
  actionError,
  onAction,
}: {
  org: OrgResponse;
  actionPending: boolean;
  activeAction: ActiveOrgAction | null;
  actionError: OrgActionError | null;
  onAction: (
    variables: OrgActionVariables,
    onSuccess?: () => void,
    onStale?: () => void,
  ) => void;
}) {
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [suspendOpen, setSuspendOpen] = useState(false);
  const actionForThisOrg = activeAction?.orgId === org.org_id;
  const pendingKind = actionForThisOrg ? activeAction.kind : null;
  const errorForThisOrg =
    actionError?.orgId === org.org_id ? actionError : null;
  const immediateError =
    errorForThisOrg?.kind === "start-review" ||
    errorForThisOrg?.kind === "reactivate"
      ? errorForThisOrg.message
      : null;
  const hasActions =
    org.status === "pending" ||
    org.status === "in_review" ||
    org.status === "active" ||
    org.status === "suspended";
  const approvalEligible =
    org.can_approve && (org.status === "pending" || org.status === "in_review");

  if (!hasActions && !errorForThisOrg) return null;

  return (
    <>
      <Paper
        component="section"
        aria-label="Organization actions"
        variant="outlined"
        sx={{ p: 2 }}
      >
        <Stack spacing={1.5}>
          {immediateError && (
            <Alert severity="error" role="alert">
              {immediateError}
            </Alert>
          )}
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={{ flexWrap: "wrap" }}
          >
            {org.status === "pending" && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={actionPending}
                  onClick={() =>
                    onAction({ kind: "start-review", orgId: org.org_id })
                  }
                >
                  {pendingKind === "start-review"
                    ? "Starting…"
                    : "Start Review"}
                </Button>
                {org.can_approve && (
                  <Button
                    size="small"
                    variant="contained"
                    disabled={actionPending}
                    onClick={() => setApproveOpen(true)}
                  >
                    Approve Launch
                  </Button>
                )}
              </>
            )}
            {org.status === "in_review" && (
              <>
                {org.can_approve && (
                  <Button
                    size="small"
                    variant="contained"
                    disabled={actionPending}
                    onClick={() => setApproveOpen(true)}
                  >
                    Approve Launch
                  </Button>
                )}
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  disabled={actionPending}
                  onClick={() => setRejectOpen(true)}
                >
                  Reject
                </Button>
              </>
            )}
            {org.status === "active" && (
              <Button
                size="small"
                color="error"
                variant="outlined"
                disabled={actionPending}
                onClick={() => setSuspendOpen(true)}
              >
                Suspend
              </Button>
            )}
            {org.status === "suspended" && (
              <Button
                size="small"
                variant="contained"
                disabled={actionPending}
                onClick={() =>
                  onAction({ kind: "reactivate", orgId: org.org_id })
                }
              >
                {pendingKind === "reactivate" ? "Reactivating…" : "Reactivate"}
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      {approveOpen && approvalEligible && (
        <ApprovalDialog
          open
          pending={actionPending && pendingKind === "approve"}
          failed={errorForThisOrg?.kind === "approve"}
          onCancel={() => setApproveOpen(false)}
          onConfirm={() =>
            onAction({ kind: "approve", orgId: org.org_id }, () =>
              setApproveOpen(false),
            )
          }
        />
      )}
      <ReasonDialog
        open={rejectOpen}
        kind="reject"
        pending={actionPending && pendingKind === "reject"}
        failed={errorForThisOrg?.kind === "reject"}
        onCancel={() => setRejectOpen(false)}
        onConfirm={(reason) =>
          onAction({ kind: "reject", orgId: org.org_id, reason }, () =>
            setRejectOpen(false),
          )
        }
      />
      <ReasonDialog
        open={suspendOpen}
        kind="suspend"
        pending={actionPending && pendingKind === "suspend"}
        failed={errorForThisOrg?.kind === "suspend"}
        onCancel={() => setSuspendOpen(false)}
        onConfirm={(reason) =>
          onAction({ kind: "suspend", orgId: org.org_id, reason }, () =>
            setSuspendOpen(false),
          )
        }
      />
    </>
  );
}
